/**
 * Movimento — helpers do design system de movimento (decisões de 23/10/2026; Motion para React,
 * decisão M4 b). O CSS (styles/movimento.css) faz abrir e fechar, laços e troca no lugar; isto
 * cobre o que o CSS não faz sozinho: contagem, voo e as transições do Motion com os mesmos
 * tokens de tokens.css (--duracao-*, --curva-*).
 *
 * Fora do navegador (testes, jsdom sem matchMedia nem CSS), os tokens valem 0: tudo troca na hora.
 */
import { animate, type Transition } from 'motion/react';

/** "Reduzir movimento" do sistema ou a opção do produto (<html data-movimento="reduzido">). */
export function semMovimento(): boolean {
  const media =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return media || document.documentElement.dataset.movimento === 'reduzido';
}

/**
 * Duração de um token em segundos ("200ms" → 0.2; "2s" → 2). Vale para --duracao-*, --espera-*,
 * --permanencia-* e --laco-*. Lê do CSS para que o modo reduzido (120 ms) valha aqui também.
 */
export function token(nome: string, el: Element = document.documentElement): number {
  const bruto = getComputedStyle(el).getPropertyValue(nome).trim();
  const valor = parseFloat(bruto);
  if (!Number.isFinite(valor)) return 0;
  return bruto.endsWith('ms') ? valor / 1000 : bruto.endsWith('s') ? valor : valor / 1000;
}

/** O mesmo token em milissegundos (para setTimeout). */
export function tokenMs(nome: string, el?: Element): number {
  return Math.round(token(nome, el) * 1000);
}

type Bezier = [number, number, number, number];

export const curva: Record<'entra' | 'sai' | 'move' | 'enfase', Bezier> = {
  entra: [0.2, 0.8, 0.2, 1], // o que aparece
  sai: [0.4, 0, 1, 1], // o que some
  move: [0.4, 0, 0.2, 1], // o que muda de lugar ou tamanho
  enfase: [0.2, 0.9, 0.3, 1.25], // N4, N5 e o encaixe do funil
};

/** Transições prontas. A saída usa o degrau de baixo da entrada. */
export const t = {
  entra: (d = '--duracao-200'): Transition => ({ duration: token(d), ease: curva.entra }),
  sai: (d = '--duracao-140'): Transition => ({ duration: token(d), ease: curva.sai }),
  move: (d = '--duracao-280'): Transition => ({ duration: token(d), ease: curva.move }),
  enfase: (d = '--duracao-400'): Transition => ({ duration: token(d), ease: curva.enfase }),
  // Mola só no encaixe do funil (M11 b). Valores de partida: ajustar quando o funil existir.
  encaixe: { type: 'spring', stiffness: 520, damping: 34 } as Transition,
};

/**
 * Contagem que sobe (N4): só números da pessoa. O leitor de tela recebe só o final, num
 * role="status" à parte. Sem movimento (ou sem duração), o número final na hora.
 */
export function contarAte(
  el: HTMLElement,
  de: number,
  ate: number,
  formato: (n: number) => string = (n) => n.toLocaleString('pt-BR'),
  duracao = token('--duracao-700'),
): { stop: () => void } | undefined {
  if (semMovimento() || duracao === 0 || de === ate) {
    el.textContent = formato(ate);
    return undefined;
  }
  return animate(de, ate, {
    duration: duracao,
    ease: curva.entra,
    onUpdate: (v) => {
      el.textContent = formato(Math.round(v));
    },
  });
}

/**
 * Voo até "Sua busca" (N4): o cartão da vitrine fica onde está, então quem voa é um fantasma
 * (cópia do ícone e do nome, position: fixed) do cartão até a linha nova. Abra o espaço da linha
 * antes (.ds-altura) e mostre a linha quando o voo terminar.
 */
export async function voar(fantasma: HTMLElement, origem: DOMRect, destino: DOMRect): Promise<void> {
  if (semMovimento()) return;
  const d = token('--duracao-400');
  if (d === 0) return;
  const dx = destino.left - origem.left;
  const dy = destino.top - origem.top;
  animate(fantasma, { opacity: [0, 1, 1, 0] }, { duration: d, times: [0, 0.12, 0.82, 1], ease: 'linear' });
  await animate(fantasma, { x: [0, dx], y: [0, dy] }, { duration: d, ease: curva.move });
}

/** Fluxo com teto: no máximo `porSegundo` entregas por segundo; o resto chega junto, como "+N". */
export function comTeto<T>(entregar: (lote: T[]) => void, porSegundo = 4): (item: T) => void {
  let fila: T[] = [];
  let ultimo = 0;
  let agendado = 0;
  const soltar = (): void => {
    agendado = 0;
    ultimo = performance.now();
    const lote = fila;
    fila = [];
    entregar(lote);
  };
  return (item: T) => {
    fila.push(item);
    const espera = Math.max(0, 1000 / porSegundo - (performance.now() - ultimo));
    if (!agendado) agendado = window.setTimeout(soltar, espera);
  };
}
